import {
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@data-products-and-ai/react-components';
import { ReactNode, useCallback, useState } from 'react';

type ChipColor =
  | 'primary'
  | 'default'
  | 'error'
  | 'success'
  | 'warning'
  | 'highlight';

type TooltipColor = 'dark' | 'light';

interface CollapsibleChipListProps<T> {
  items: T[];
  maxVisible?: number;
  tooltipColor?: TooltipColor;
  getKey: (item: T, index?: number) => string | number;
  getLabel: (item: T) => string;
  getTooltip?: (item: T) => ReactNode;
  getColor?: (item: T) => ChipColor;
  onClick?: (item: T) => void;
}

const DEFAULT_MAX_VISIBLE = 5;

export const CollapsibleChipList = <T,>({
  items,
  maxVisible = DEFAULT_MAX_VISIBLE,
  tooltipColor,
  getKey,
  getLabel,
  getTooltip,
  getColor,
  onClick,
}: CollapsibleChipListProps<T>) => {
  const [showAll, setShowAll] = useState(false);

  if (!items?.length) return <Typography>Not available</Typography>;

  const visibleItems = showAll ? items : items.slice(0, maxVisible);
  const remainingCount = items.length - maxVisible;

  const handleToggle = useCallback(() => {
    setShowAll((prev) => !prev);
  }, []);

  return (
    <Stack direction="column" verticalAlign="top" gap={8}>
      <Stack direction="row" verticalAlign="center" wrap gap={8}>
        {visibleItems.map((item, index) => {
          const label = getLabel(item);
          const tooltip = getTooltip?.(item) ?? label;
          return (
            <Tooltip
              key={getKey(item, index)}
              arrow
              title={tooltip}
              color={tooltipColor ?? 'dark'}
            >
              <Chip
                label={label}
                chipType="thin"
                size="extra-small"
                color={getColor?.(item) ?? 'primary'}
                {...(onClick && { onClick: () => onClick(item) })}
              />
            </Tooltip>
          );
        })}
      </Stack>

      {remainingCount > 0 && (
        <Button
          type="link"
          allCaps={false}
          textColor="var(--color-primary)"
          onClick={handleToggle}
        >
          <Typography tag="textsmall">
            {showAll ? 'Show less' : 'Show more'}
          </Typography>
        </Button>
      )}
    </Stack>
  );
};
